import {Injectable} from '@angular/core';
import {NumberForDisplayParams} from "../types/utility.types";
import {RecordDataValue, RecordsDataRequestQueryParams} from "../types/records-table.types";
import {REGEX} from "../constants/regex-constants";
import {BudgetFormatOptions} from "../models/budget-format.model";
import {FieldClassProps} from "../components/modals/base-modal/types/base-modal.types";

@Injectable({
  providedIn: 'root'
})
export class UtilityService {

  private PHONE_NUMBER_REGEX = /^\d{10,15}$/;

  public removeEmptyProperties(obj: object) {
    return Object.fromEntries(
      Object.entries(obj).filter(([_, v]) => {
        if (v == null || v === '') return false;
        if (typeof v === 'string') return !!v.length;
        if (Array.isArray(v)) return !!v.length;
        return true;
      })
    );
  }

  public clearFilters(recordsDataRequestQueryParams: RecordsDataRequestQueryParams): RecordsDataRequestQueryParams {
    return {
      size: recordsDataRequestQueryParams.size,
      showMistakes: recordsDataRequestQueryParams.showMistakes,
      showProcessed: recordsDataRequestQueryParams.showProcessed,
      sort: recordsDataRequestQueryParams.sort,
      page: 0
    };
  }

  public isObjectEmpty(object: object): boolean {
    return !Object.keys(object).length;
  }

  public hexToRgba(hex: string, alpha: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);

    if (alpha) {
      return 'rgba(' + r + ', ' + g + ', ' + b + ', ' + alpha + ')';
    } else {
      return 'rgb(' + r + ', ' + g + ', ' + b + ')';
    }
  }

  public downloadExcelFile(blobPart: BlobPart, fileName: string): void {
    const blob: Blob = new Blob([blobPart], { type: 'octet/stream' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');

    document.body.appendChild(a);
    a.href = url;
    a.download = `${fileName}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  public isArrayContainsAnyElementOfAnotherArray(firstArray: Array<unknown>, secondArray: Array<unknown>): boolean {
    return firstArray.some((element: unknown) => secondArray.includes(element));
  }

  public getQueryParamsFromLink(link: string): Record<string, string> {
    if (!link) { return {}; }

    const queryParamsObject: Record<string, string> = {};

    link.split('?')[1]?.split('&').map((queryParam: string) => {
      const [queryParamName, queryParamValue] = queryParam?.split('=');

      queryParamsObject[queryParamName] = queryParamValue;
    })

    return queryParamsObject;
  }

  public updateTooltipStyle(property: string, value: string): void {
    document.documentElement.style.setProperty(property, value);
  }

  public isValidPhoneNumber(phoneNumber: string | null): boolean {
    return !!phoneNumber && this.PHONE_NUMBER_REGEX.test(phoneNumber);
  }

  public formatNumberForDisplay(numberForDisplayParams: NumberForDisplayParams): string {
    const fractionalValue = numberForDisplayParams.fractional?.value;
    const numberSeparator = numberForDisplayParams.numberSeparator;
    const options: Intl.NumberFormatOptions = {
      minimumFractionDigits: fractionalValue ?? 0,
      maximumFractionDigits: fractionalValue ?? 0,
      useGrouping: !!numberSeparator
    }

    let formattedNumber = new Intl.NumberFormat('en-US', options).format(numberForDisplayParams.value);
    if (numberSeparator) {
      formattedNumber = formattedNumber.replace(/,/g, numberSeparator.separator === ' ' ? '\u00A0' : numberSeparator.separator);
    }

    return formattedNumber;
  }

  public formatBudgetValue(options: BudgetFormatOptions): string {
    const {
      value,
      maximumFractionDigits = 0,
      showCurrencySymbol = true,
      currency = 'RUB',
      locale = 'ru-RU'
    } = options;

    const formatOptions: Intl.NumberFormatOptions = {
      style: showCurrencySymbol ? 'currency' : 'decimal',
      currency: showCurrencySymbol ? currency : undefined,
      maximumFractionDigits: maximumFractionDigits,
      minimumFractionDigits: maximumFractionDigits
    };

    return new Intl.NumberFormat(locale, formatOptions).format(value);
  }

  public isActiveLinkRowForUrlType(value: string, isEditModeActive: boolean): boolean {
    if (!value) { return false; }

    return !isEditModeActive && this.isValidUrl(value.trim())
  }

  public convertToDate(dateValue?: RecordDataValue): Date | null {
    if (typeof dateValue !== 'string') { return null; }

    const [day, month, year] = dateValue.split('.').map(Number);

    return new Date(year, month - 1, day);
  }

  public formatDateToRussianStyle(date: Date): string {
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date).split('/').join('.');
  }

  public formatDateTimeToRussianStyle(dateValue?: string): string | null {
    if (!dateValue) { return null; }

    const date = new Date(dateValue);
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(date).replace(',', '');
  }

  public dateToTimeString(date: Date | string): string | null {
    if (!date) { return null; }
    if (typeof date === 'string') {
      date = this.parseTimeStringToDate(date);
    }

    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  }

  public formatArrayToRussianDate(dateArray: number[]): string {
    if (!dateArray || dateArray.length < 3) { return ''; }

    const date = new Date(dateArray[0], dateArray[1] - 1, dateArray[2]);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
  }

  public formatArrayToRussianDateTime(dateArray: number[]): string {
    if (!dateArray || dateArray.length < 7) { return ''; }

    const [year, month, day, hours, minutes] = dateArray;
    const date = new Date(year, month - 1, day, hours, minutes);
    const formattedDay = date.getDate().toString().padStart(2, '0');
    const formattedMonth = (date.getMonth() + 1).toString().padStart(2, '0');
    const formattedYear = date.getFullYear();
    const formattedHours = date.getHours().toString().padStart(2, '0');
    const formattedMinutes = date.getMinutes().toString().padStart(2, '0');

    return `${formattedDay}.${formattedMonth}.${formattedYear} ${formattedHours}:${formattedMinutes}`;
  }

  public isFieldFullFilled(fieldClassProps: FieldClassProps): boolean {
    const { formControlName, formGroup } = fieldClassProps;

    if (!formControlName || !formGroup) { return false; }

    return !!formGroup.get(formControlName)?.value;
  }

  public formatDateForApi(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  public parseTimeStringToDate(timeString: string): Date {
      const [hoursStr, minutesStr] = timeString.split(':');
      const hours = parseInt(hoursStr, 10);
      const minutes = parseInt(minutesStr, 10);

      const now = new Date();
      return new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        hours,
        minutes,
        0,
        0
      );
  }

  //проверяет, ссылка в интупе или нет.
  private isValidUrl(url: string): boolean {
    if (!url) { return false; }

    return REGEX.URL_REGEX.test(url);
  }
}
